import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronLeft, ChevronRight, X } from 'lucide-react';
import type { PrototypeDesign } from '@aiai/shared';
import { previewTokens, type PreviewTokens } from './designTokens';

/*
 * A fake, fully placeholder SaaS app themed purely from a design's preview
 * tokens — nav, sidebar, buttons, stats, table, form controls, alert, tabs,
 * dialog, empty state and typography — so a style can be judged on a whole
 * page instead of a thumbnail. Inline styles only: the platform's Tailwind
 * theme must not leak into the previewed look.
 */

function Card({ t, children, style }: { t: PreviewTokens; children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: t.surface,
        border: t.hairline,
        borderRadius: t.radius,
        boxShadow: t.shadow,
        padding: 16,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Heading({ t, as: Tag = 'h3', size, children }: { t: PreviewTokens; as?: 'h1' | 'h2' | 'h3'; size: number; children: ReactNode }) {
  return (
    <Tag
      style={{
        margin: 0,
        fontSize: size,
        lineHeight: 1.2,
        fontWeight: t.headingWeight,
        letterSpacing: t.headingTracking,
      }}
    >
      {children}
    </Tag>
  );
}

type ButtonKind = 'primary' | 'secondary' | 'ghost';

function buttonStyle(t: PreviewTokens, kind: ButtonKind): CSSProperties {
  const base: CSSProperties = {
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    padding: '8px 14px',
    borderRadius: t.controlRadius,
    cursor: 'default',
    lineHeight: 1.2,
  };
  if (kind === 'primary') {
    return { ...base, background: t.accent, color: t.onAccent, border: t.border ?? '1px solid transparent', boxShadow: t.shadow };
  }
  if (kind === 'secondary') {
    return { ...base, background: t.surface, color: t.text, border: t.hairline };
  }
  return { ...base, background: 'transparent', color: t.text, border: '1px solid transparent' };
}

function Btn({ t, kind = 'primary', children }: { t: PreviewTokens; kind?: ButtonKind; children: ReactNode }) {
  return (
    <button type="button" tabIndex={-1} style={buttonStyle(t, kind)}>
      {children}
    </button>
  );
}

function Badge({ t, tone, children }: { t: PreviewTokens; tone: 'accent' | 'muted'; children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 999,
        background: tone === 'accent' ? t.accentSoft : t.subtle,
        color: tone === 'accent' ? t.text : t.muted,
        border: `1px solid ${tone === 'accent' ? t.accent : t.divider}`,
      }}
    >
      {children}
    </span>
  );
}

function inputStyle(t: PreviewTokens): CSSProperties {
  return {
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    fontSize: 13,
    padding: '8px 10px',
    borderRadius: t.controlRadius,
    border: t.hairline,
    background: t.bg,
    color: t.text,
  };
}

function Label({ t, children }: { t: PreviewTokens; children: ReactNode }) {
  return (
    <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: t.muted, marginBottom: 4 }}>
      {children}
    </span>
  );
}

const ROWS: Array<[string, string, string, boolean]> = [
  ['Acme Corp', 'Ada Lovelace', '$12,400', true],
  ['Globex', 'Grace Hopper', '$8,950', true],
  ['Initech', 'Alan Turing', '$3,120', false],
  ['Umbrella', 'Katherine Johnson', '$21,780', true],
];

export function DesignPreviewApp({ design }: { design: PrototypeDesign }) {
  const t = previewTokens(design.preview);
  const [tab, setTab] = useState(0);
  const [toggle, setToggle] = useState(true);

  return (
    <div
      data-testid="design-preview-app"
      style={{ background: t.bg, color: t.text, fontFamily: t.fontFamily, minHeight: '100%', fontSize: 14, lineHeight: 1.5 }}
    >
      {/* Top nav */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 20,
          padding: '12px 24px',
          background: t.surface,
          borderBottom: t.hairline,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: t.headingWeight, letterSpacing: t.headingTracking }}>
          <span style={{ width: 22, height: 22, borderRadius: Math.min(t.radius, 6), background: t.accent, border: t.border }} />
          Northwind
        </span>
        <nav style={{ display: 'flex', gap: 16, fontSize: 13, color: t.muted }}>
          <span style={{ color: t.text, fontWeight: 600 }}>Dashboard</span>
          <span>Customers</span>
          <span>Reports</span>
          <span>Settings</span>
        </nav>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <input style={{ ...inputStyle(t), width: 180 }} placeholder="Search…" tabIndex={-1} readOnly />
          <span
            aria-hidden="true"
            style={{
              width: 30,
              height: 30,
              borderRadius: 999,
              background: t.accentSoft,
              border: `1px solid ${t.accent}`,
              display: 'grid',
              placeItems: 'center',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            JD
          </span>
        </span>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar */}
        <aside
          className="md:w-52 md:flex-none"
          style={{ padding: 16, borderRight: t.hairline, background: t.surface }}
        >
          <p style={{ margin: '0 0 8px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: t.faint }}>
            Workspace
          </p>
          {['Overview', 'Pipeline', 'Invoices', 'Team', 'Integrations'].map((item, i) => (
            <div
              key={item}
              style={{
                padding: '7px 10px',
                marginBottom: 2,
                borderRadius: t.controlRadius,
                fontSize: 13,
                fontWeight: i === 0 ? 600 : 400,
                background: i === 0 ? t.accentSoft : 'transparent',
                color: i === 0 ? t.text : t.muted,
                borderLeft: i === 0 ? `3px solid ${t.accent}` : '3px solid transparent',
              }}
            >
              {item}
            </div>
          ))}
        </aside>

        <main style={{ flex: 1, minWidth: 0, padding: 24, display: 'grid', gap: 20 }}>
          {/* Page header */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <p style={{ margin: 0, fontSize: 12, color: t.faint }}>Home / Dashboard</p>
              <Heading t={t} as="h1" size={28}>
                Good morning, Jane
              </Heading>
              <p style={{ margin: '4px 0 0', color: t.muted }}>Here’s what happened across your workspace this week.</p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Btn t={t} kind="ghost">
                Export
              </Btn>
              <Btn t={t} kind="secondary">
                Share
              </Btn>
              <Btn t={t}>New invoice</Btn>
            </div>
          </div>

          {/* Alert */}
          <div
            role="presentation"
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              padding: '10px 14px',
              borderRadius: t.radius,
              background: t.accentSoft,
              border: `1px solid ${t.accent}`,
              fontSize: 13,
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: 999, background: t.accent, flex: 'none' }} />
            <span>
              <strong>Heads up:</strong> your trial ends in 5 days. <span style={{ color: t.accent, textDecoration: 'underline' }}>Upgrade now</span>
            </span>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
            {[
              ['Revenue', '$48,250', '+12.4%'],
              ['Active users', '2,340', '+3.1%'],
              ['Churn', '1.8%', '−0.4%'],
              ['NPS', '62', '+5'],
            ].map(([label, value, delta]) => (
              <Card key={label} t={t}>
                <p style={{ margin: 0, fontSize: 12, color: t.muted }}>{label}</p>
                <p style={{ margin: '4px 0', fontSize: 24, fontWeight: t.headingWeight, letterSpacing: t.headingTracking }}>{value}</p>
                <Badge t={t} tone="accent">
                  {delta}
                </Badge>
              </Card>
            ))}
          </div>

          <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {/* Table */}
            <Card t={t} style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: `1px solid ${t.divider}` }}>
                <Heading t={t} size={16}>
                  Recent deals
                </Heading>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: t.faint, textAlign: 'left' }}>
                    {['Company', 'Owner', 'Amount', 'Status'].map((h) => (
                      <th key={h} style={{ padding: '8px 16px', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(([company, owner, amount, paid], i) => (
                    <tr key={company} style={{ borderTop: `1px solid ${t.divider}`, background: i % 2 ? t.subtle : 'transparent' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 600 }}>{company}</td>
                      <td style={{ padding: '10px 16px', color: t.muted }}>{owner}</td>
                      <td style={{ padding: '10px 16px', fontVariantNumeric: 'tabular-nums' }}>{amount}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <Badge t={t} tone={paid ? 'accent' : 'muted'}>
                          {paid ? 'Paid' : 'Pending'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>

            {/* Form */}
            <Card t={t}>
              <Heading t={t} size={16}>
                Invite a teammate
              </Heading>
              <form style={{ display: 'grid', gap: 12, marginTop: 12 }} onSubmit={(e) => e.preventDefault()}>
                <label>
                  <Label t={t}>Email</Label>
                  <input style={inputStyle(t)} defaultValue="sam@example.com" tabIndex={-1} />
                </label>
                <label>
                  <Label t={t}>Role</Label>
                  <select style={inputStyle(t)} defaultValue="editor" tabIndex={-1}>
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                </label>
                <label>
                  <Label t={t}>Message</Label>
                  <textarea style={{ ...inputStyle(t), minHeight: 64, resize: 'none' }} defaultValue="Welcome aboard!" tabIndex={-1} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" defaultChecked tabIndex={-1} style={{ accentColor: t.accent }} />
                  Send a welcome email
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                  <span
                    role="presentation"
                    onClick={() => setToggle((v) => !v)}
                    style={{
                      width: 36,
                      height: 20,
                      borderRadius: 999,
                      padding: 2,
                      boxSizing: 'border-box',
                      background: toggle ? t.accent : t.divider,
                      border: t.border,
                      display: 'flex',
                      justifyContent: toggle ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <span style={{ width: 14, height: 14, borderRadius: 999, background: toggle ? t.onAccent : t.surface }} />
                  </span>
                  Grant billing access
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <Btn t={t} kind="ghost">
                    Cancel
                  </Btn>
                  <Btn t={t}>Send invite</Btn>
                </div>
              </form>
            </Card>
          </div>

          {/* Tabs */}
          <Card t={t}>
            <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${t.divider}`, marginBottom: 12 }}>
              {['Activity', 'Comments', 'Files'].map((label, i) => (
                <span
                  key={label}
                  role="presentation"
                  onClick={() => setTab(i)}
                  style={{
                    padding: '8px 12px',
                    fontSize: 13,
                    fontWeight: tab === i ? 600 : 400,
                    color: tab === i ? t.text : t.muted,
                    borderBottom: `2px solid ${tab === i ? t.accent : 'transparent'}`,
                    marginBottom: -1,
                    cursor: 'pointer',
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
            {[
              ['Ada', 'closed the Globex deal', '2m ago'],
              ['Grace', 'commented on Invoice #1042', '1h ago'],
              ['Alan', 'uploaded Q3-forecast.pdf', 'Yesterday'],
            ].map(([who, what, when]) => (
              <div key={what} style={{ display: 'flex', gap: 8, padding: '6px 0', fontSize: 13 }}>
                <strong>{who}</strong>
                <span style={{ color: t.muted }}>{what}</span>
                <span style={{ marginLeft: 'auto', color: t.faint, fontSize: 12 }}>{when}</span>
              </div>
            ))}
          </Card>

          <div style={{ display: 'grid', gap: 20, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            {/* Dialog */}
            <div style={{ background: t.subtle, borderRadius: t.radius, padding: 20, display: 'grid', placeItems: 'center' }}>
              <Card t={t} style={{ width: '100%', maxWidth: 320, boxShadow: t.shadow ?? `0 12px 32px ${t.divider}` }}>
                <Heading t={t} size={16}>
                  Delete this project?
                </Heading>
                <p style={{ margin: '6px 0 14px', fontSize: 13, color: t.muted }}>
                  This can’t be undone. All invoices will be archived.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <Btn t={t} kind="secondary">
                    Keep it
                  </Btn>
                  <Btn t={t}>Delete</Btn>
                </div>
              </Card>
            </div>

            {/* Empty state */}
            <Card t={t} style={{ textAlign: 'center', padding: 28, border: `1px dashed ${t.faint}` }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  margin: '0 auto 10px',
                  borderRadius: 999,
                  background: t.accentSoft,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 20,
                }}
                aria-hidden="true"
              >
                ✦
              </div>
              <Heading t={t} size={16}>
                No reports yet
              </Heading>
              <p style={{ margin: '6px 0 14px', fontSize: 13, color: t.muted }}>Create your first report to see trends here.</p>
              <Btn t={t}>Create report</Btn>
            </Card>

            {/* Typography */}
            <Card t={t}>
              <Heading t={t} as="h1" size={30}>
                Heading one
              </Heading>
              <Heading t={t} as="h2" size={22}>
                Heading two
              </Heading>
              <Heading t={t} as="h3" size={17}>
                Heading three
              </Heading>
              <p style={{ margin: '8px 0 4px' }}>
                Body text reads like this, with a{' '}
                <span style={{ color: t.accent, textDecoration: 'underline' }}>link</span> and some{' '}
                <code style={{ fontFamily: "ui-monospace, Consolas, monospace", fontSize: 12, padding: '1px 5px', borderRadius: 4, background: t.subtle }}>
                  inline code
                </code>
                .
              </p>
              <p style={{ margin: 0, fontSize: 13, color: t.muted }}>Muted secondary text for hints and captions.</p>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * Near-fullscreen layer showing {@link DesignPreviewApp} for one design, with
 * prev/next stepping through `designs` and an optional "Select this style".
 * Sits above the picker Modal (z-50) and swallows Escape so closing the
 * preview never also closes the picker underneath.
 */
export function DesignPreviewOverlay({
  designs,
  designId,
  onNavigate,
  onClose,
  selectedId,
  onChoose,
}: {
  designs: PrototypeDesign[];
  designId: string;
  onNavigate: (designId: string) => void;
  onClose: () => void;
  /** Currently highlighted design — shows "Selected" instead of the choose button. */
  selectedId?: string | null;
  /** When given, offers "Select this style" (highlights only; never commits). */
  onChoose?: (designId: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const index = designs.findIndex((d) => d.id === designId);
  const design = designs[index];
  const canStep = designs.length > 1 && index >= 0;

  const step = (delta: number) => {
    if (!canStep) return;
    onNavigate(designs[(index + delta + designs.length) % designs.length].id);
  };

  // Latest handlers for the window listener without re-binding every render.
  const handlers = useRef({ step, onClose });
  handlers.current = { step, onClose };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handlers.current.onClose();
      else if (e.key === 'ArrowLeft') handlers.current.step(-1);
      else if (e.key === 'ArrowRight') handlers.current.step(1);
      else return;
      // Capture phase: keep the picker Modal's own Escape handler from firing.
      e.stopPropagation();
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    ref.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // Each step starts the preview at the top.
  useEffect(() => {
    ref.current?.querySelector('[data-preview-scroll]')?.scrollTo?.(0, 0);
  }, [designId]);

  if (!design) return null;
  const selected = selectedId === design.id;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label={`Full-page preview: ${design.name}`}
      data-testid="design-preview-overlay"
      tabIndex={-1}
      className="fixed inset-0 z-[60] flex flex-col bg-black/70 outline-none sm:p-4"
    >
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-1 px-4 py-2.5 sm:rounded-t-[8px]">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-sm font-semibold text-ink">{design.name}</h2>
          <p className="truncate text-xs text-ink-faint">{design.tagline}</p>
        </div>
        {canStep && (
          <div className="flex items-center gap-1">
            <button type="button" className="btn-secondary px-2" onClick={() => step(-1)} aria-label="Previous style">
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="min-w-12 text-center text-xs tabular-nums text-ink-faint">
              {index + 1} / {designs.length}
            </span>
            <button type="button" className="btn-secondary px-2" onClick={() => step(1)} aria-label="Next style">
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        )}
        {onChoose &&
          (selected ? (
            <span className="flex items-center gap-1 px-2 text-xs font-medium text-ink-muted">
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              Selected
            </span>
          ) : (
            <button type="button" className="btn-primary" onClick={() => onChoose(design.id)}>
              Select this style
            </button>
          ))}
        <button
          type="button"
          className="rounded-[5px] p-1.5 text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
          onClick={onClose}
          aria-label="Close preview"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      <div data-preview-scroll className="min-h-0 flex-1 overflow-y-auto sm:rounded-b-[8px]">
        <DesignPreviewApp design={design} />
      </div>
    </div>,
    document.body,
  );
}
